import { api } from './api';
import auth from './auth';

/**
 * 앱 안 정기결제 (2026-10-10 사장님: 앱에서 바로 구독, 웹과 같은 월 3,300원).
 * 안드로이드 앱 2.1.0 부터 구글 플레이 결제. 앱(android-app/Billing.kt)과 window.chordscore.postMessage / window.csIap 로 주고받는다.
 * 아이폰은 맥 빌드 때 같은 방식으로 붙인다.
 */

declare global {
  interface Window {
    csIap?: {
      onInfo?: (d: { ok: boolean; price: string; trial?: boolean }) => void;
      onPurchase?: (d: { productId: string; purchaseToken: string }) => void;
      onError?: (d: { code: string; message: string }) => void;
    };
    chordscore: any;
  }
}

const appVersion = () => (navigator.userAgent.match(/csapp\/(\d+)\.(\d+)\.(\d+)/) || []).slice(1).map(Number);

/** 이 앱에서 스토어 결제로 바로 구독할 수 있는지 */
export const canAppPurchase = () => {
  if (!/chordscore\/Android/.test(navigator.userAgent) || !window.chordscore) return false;
  const v = appVersion();
  return v.length === 3 && (v[0] > 2 || (v[0] === 2 && v[1] >= 1));
};

const post = (msg: object) => window.chordscore && window.chordscore.postMessage(JSON.stringify(msg));

type Listener = (e: { type: 'done' | 'error' | 'info'; message?: string; price?: string; trial?: boolean }) => void;
const listeners = new Set<Listener>();
export const onIap = (fn: Listener) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
const emit: Listener = (e) => listeners.forEach((fn) => fn(e));

const verify = async (productId: string, purchaseToken: string) => {
  try {
    const { data } = await api.post('/iap/google/verify', { product_id: productId, purchase_token: purchaseToken });
    if (data.success) {
      if (data.data && data.data.pay_status !== null && data.data.pay_status !== undefined) {
        auth.setPayStatus(data.data.pay_status);
      }
      emit({ type: 'done' });
    } else {
      emit({ type: 'error', message: data.alert || '결제를 확인하지 못했어요.' });
    }
  } catch (e) {
    emit({ type: 'error', message: '결제 확인이 늦어지고 있어요. 잠시 뒤 앱을 다시 열어 주세요. 결제는 안전하게 남아 있어요.' });
  }
};

let installed = false;
/** 앱 시작 때 한 번: 앱의 결제 결과를 받을 곳을 만들고, 확인 못 한 구매가 있으면 마저 확인 */
export const installIap = () => {
  if (installed || !canAppPurchase()) return;
  installed = true;
  window.csIap = {
    onInfo: (d) => emit({ type: 'info', price: d.ok ? d.price : '', trial: !!d.trial }), // trial: 첫 달 무료를 받을 수 있는 사람 (앱 2.1.1+)
    onPurchase: (d) => {
      if (auth.getToken()) verify(d.productId, d.purchaseToken);
    },
    onError: (d) => {
      if (d.code === 'CANCELED') return emit({ type: 'error', message: '' });
      emit({ type: 'error', message: d.message && d.code === 'PENDING' ? d.message : '결제를 진행하지 못했어요. 잠시 뒤 다시 시도해 주세요.' });
    },
  };
  if (auth.getToken()) post({ type: 'iapPending' });
};

export const requestIapInfo = () => post({ type: 'iapInfo' });

export const startAppSubscription = async () => {
  const { data } = await api.get('/user');
  post({ type: 'iapSubscribe', userId: String(data.data.user.id) });
};
